import React from "react";
import {
  AbsoluteFill,
  Easing,
  interpolate,
  Sequence,
  spring,
  useCurrentFrame,
} from "remotion";

const T = {
  paper: "#E7E4DD",
  ink: "#11110F",
  soft: "#B9B5AD",
  red: "#D64A3A",
  white: "#F4F1EA",
};

const clamp = {
  extrapolateLeft: "clamp" as const,
  extrapolateRight: "clamp" as const,
};

const ease = Easing.bezier(0.16, 1, 0.3, 1);
const cameraEase = Easing.bezier(0.65, 0, 0.35, 1);

const p = (
  frame: number,
  from: number,
  to: number,
  easing: (value: number) => number = ease,
) =>
  interpolate(frame, [from, to], [0, 1], {
    ...clamp,
    easing,
  });

const windowOpacity = (
  frame: number,
  enter: [number, number],
  exit: [number, number],
) =>
  interpolate(frame, [...enter, ...exit], [0, 1, 1, 0], {
    ...clamp,
    easing: cameraEase,
  });

const Grain: React.FC<{dark?: boolean}> = ({dark = false}) => {
  const frame = useCurrentFrame();

  return (
    <svg
      className="ev-grain"
      viewBox="0 0 1080 1920"
      aria-hidden="true"
      style={{opacity: dark ? 0.14 : 0.2}}
    >
      <filter id={dark ? "ev-noise-dark" : "ev-noise-light"}>
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.72"
          numOctaves="4"
          seed={(frame % 4) + 1}
          stitchTiles="stitch"
        />
      </filter>
      <rect
        width="1080"
        height="1920"
        filter={`url(#${dark ? "ev-noise-dark" : "ev-noise-light"})`}
      />
    </svg>
  );
};

const ChromeBloom: React.FC<{frame: number; compact?: boolean}> = ({
  frame,
  compact = false,
}) => {
  const rotation = interpolate(frame, [0, 180], [-16, 64], clamp);
  const petals = Array.from({length: 10});

  return (
    <svg
      viewBox="-250 -250 500 500"
      className={compact ? "ev-bloom ev-bloom-compact" : "ev-bloom"}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="ev-bloom-core">
          <stop offset="0" stopColor="#fff" />
          <stop offset="0.35" stopColor="#a8a49c" />
          <stop offset="0.75" stopColor="#33322f" />
          <stop offset="1" stopColor="#0d0d0c" />
        </radialGradient>
        <linearGradient id="ev-petal" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f7f4ed" />
          <stop offset="0.42" stopColor="#8e8b84" />
          <stop offset="0.7" stopColor="#272724" />
          <stop offset="1" stopColor="#d5d1c8" />
        </linearGradient>
        <filter id="ev-bloom-shadow">
          <feDropShadow dx="16" dy="26" stdDeviation="18" floodColor="#000" floodOpacity="0.45" />
        </filter>
      </defs>
      <g transform={`rotate(${rotation})`} filter="url(#ev-bloom-shadow)">
        {petals.map((_, index) => (
          <ellipse
            key={index}
            cx="0"
            cy="-112"
            rx="49"
            ry="126"
            fill="url(#ev-petal)"
            stroke="#111"
            strokeWidth="2"
            transform={`rotate(${index * 36})`}
          />
        ))}
        <circle cx="0" cy="0" r="88" fill="url(#ev-bloom-core)" stroke="#111" strokeWidth="3" />
        <circle cx="-24" cy="-27" r="18" fill="#fff" opacity="0.72" />
      </g>
    </svg>
  );
};

const CurvedArrow: React.FC<{
  progress: number;
  flip?: boolean;
}> = ({progress, flip = false}) => (
  <svg
    className="ev-curved-arrow"
    viewBox="0 0 300 220"
    aria-hidden="true"
    style={{transform: flip ? "scaleX(-1)" : undefined}}
  >
    <path
      d="M 28 178 C 28 68, 112 18, 236 46"
      fill="none"
      stroke="currentColor"
      strokeWidth="5"
      strokeLinecap="round"
      strokeDasharray="420"
      strokeDashoffset={420 * (1 - progress)}
    />
    <path d="m220 26 28 22-31 16" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" opacity={progress} />
  </svg>
);

const IntroAnnotations: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = windowOpacity(frame, [0, 14], [62, 82]);
  const object = spring({
    frame,
    fps: 30,
    config: {damping: 20, stiffness: 95, mass: 1},
  });
  const arrows = p(frame, 16, 48);

  return (
    <AbsoluteFill
      className="ev-paper"
      style={{
        opacity,
        transform: `scale(${1.05 - object * 0.05})`,
      }}
    >
      <div className="ev-intro-object" style={{transform: `translateY(${(1 - object) * 120}px) scale(${0.72 + object * 0.28})`}}>
        <ChromeBloom frame={frame} />
      </div>
      <div className="ev-label ev-label-top">TACTILE<br />SHADOWS</div>
      <div className="ev-arrow-top"><CurvedArrow progress={arrows} /></div>
      <div className="ev-label ev-label-bottom">MONOCHROME<br /><strong>OBJECT STUDY</strong></div>
      <div className="ev-arrow-bottom"><CurvedArrow progress={arrows} flip /></div>
      <div className="ev-index">STUDY / 01</div>
      <Grain />
    </AbsoluteFill>
  );
};

const PortalFigure: React.FC<{frame: number}> = ({frame}) => {
  const pulse = 1 + Math.sin(frame / 9) * 0.025;

  return (
    <svg viewBox="0 0 500 620" className="ev-figure" aria-hidden="true">
      <defs>
        <linearGradient id="ev-figure-fill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f2efe7" />
          <stop offset="0.45" stopColor="#6e6c67" />
          <stop offset="1" stopColor="#151514" />
        </linearGradient>
      </defs>
      <circle cx="250" cy="170" r={105 * pulse} fill="url(#ev-figure-fill)" />
      <path d="M85 570 C105 350, 170 300, 250 300 C330 300, 395 350, 415 570Z" fill="url(#ev-figure-fill)" />
      <path d="M165 160 C190 110, 305 95, 338 175" fill="none" stroke="#111" strokeWidth="18" strokeLinecap="round" />
      <path d="M145 390 250 465 355 390" fill="none" stroke="#ddd9d0" strokeWidth="7" opacity="0.55" />
    </svg>
  );
};

const PanelReveal: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = windowOpacity(frame, [0, 16], [92, 118]);
  const enter = spring({
    frame,
    fps: 30,
    config: {damping: 22, stiffness: 100},
  });
  const split = p(frame, 42, 94, cameraEase);
  const bloom = spring({
    frame: frame - 42,
    fps: 30,
    config: {damping: 20, stiffness: 105},
  });
  const typed = Math.floor(interpolate(frame, [48, 86], [0, "SHIFT THE FRAME".length], clamp));

  return (
    <AbsoluteFill className="ev-paper" style={{opacity}}>
      <div
        className="ev-portal"
        style={{
          opacity: enter,
          transform: `translate(-50%, -50%) scale(${0.82 + enter * 0.18 + split * 0.22})`,
        }}
      >
        <PortalFigure frame={frame} />
      </div>

      <div className="ev-panel ev-panel-left" style={{transform: `translateX(${-split * 330}px)`}}>
        <PortalFigure frame={frame} />
      </div>
      <div className="ev-panel ev-panel-right" style={{transform: `translateX(${split * 330}px)`}}>
        <PortalFigure frame={frame} />
      </div>

      <div
        className="ev-panel-bloom"
        style={{
          opacity: bloom,
          transform: `translate(-50%, -50%) scale(${0.45 + bloom * 0.55}) rotate(${split * 14}deg)`,
        }}
      >
        <ChromeBloom frame={frame} compact />
      </div>

      <div className="ev-split-lines">
        <i /><i />
      </div>
      <div className="ev-panel-copy">
        <span>REARRANGE / REVEAL / REFRAME</span>
        <h2>{`SHIFT THE FRAME`.slice(0, typed)}<i /></h2>
      </div>
      <Grain />
    </AbsoluteFill>
  );
};

const LensOrb: React.FC<{frame: number}> = ({frame}) => {
  const iris = interpolate(frame, [0, 90], [0.88, 1.08], {
    ...clamp,
    easing: cameraEase,
  });
  const rotation = interpolate(frame, [0, 90], [-20, 70], clamp);

  return (
    <svg viewBox="-250 -250 500 500" className="ev-lens-orb" aria-hidden="true">
      <defs>
        <radialGradient id="ev-lens-shell">
          <stop offset="0" stopColor="#f3f0e8" />
          <stop offset="0.65" stopColor="#aaa69e" />
          <stop offset="1" stopColor="#242422" />
        </radialGradient>
        <radialGradient id="ev-lens-core">
          <stop offset="0" stopColor="#050505" />
          <stop offset="0.35" stopColor="#2b2a28" />
          <stop offset="0.7" stopColor="#bab6ae" />
          <stop offset="1" stopColor="#111" />
        </radialGradient>
        <filter id="ev-orb-shadow">
          <feDropShadow dx="22" dy="34" stdDeviation="20" floodColor="#000" floodOpacity="0.55" />
        </filter>
      </defs>
      <g filter="url(#ev-orb-shadow)">
        <circle cx="0" cy="0" r="188" fill="url(#ev-lens-shell)" />
        <g transform={`rotate(${rotation}) scale(${iris})`}>
          {Array.from({length: 14}).map((_, index) => (
            <path key={index} d="M0-122 24-42 0-20-24-42Z" fill={index % 2 ? "#1b1b19" : "#77746e"} transform={`rotate(${index * (360 / 14)})`} />
          ))}
        </g>
        <circle cx="0" cy="0" r="62" fill="url(#ev-lens-core)" />
        <circle cx="-22" cy="-25" r="16" fill="#fff" opacity="0.74" />
      </g>
    </svg>
  );
};

const LensAnalysis: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = windowOpacity(frame, [0, 14], [62, 88]);
  const object = spring({
    frame,
    fps: 30,
    config: {damping: 18, stiffness: 95},
  });
  const lines = p(frame, 18, 60);
  const camera = p(frame, 52, 88, cameraEase);

  return (
    <AbsoluteFill
      className="ev-paper"
      style={{
        opacity,
        filter: `blur(${camera * 8}px)`,
        transform: `scale(${1 + camera * 0.34})`,
      }}
    >
      <div className="ev-lens-wrap" style={{transform: `translate(-50%, -50%) scale(${0.7 + object * 0.3})`}}>
        <LensOrb frame={frame} />
      </div>
      <svg className="ev-analysis-lines" viewBox="0 0 1080 1920" aria-hidden="true">
        {[
          "M540 840 140 330",
          "M540 840 930 420",
          "M540 840 100 1120",
          "M540 840 980 1260",
        ].map((path, index) => (
          <path key={path} d={path} fill="none" stroke={T.ink} strokeWidth="3" strokeDasharray="900" strokeDashoffset={900 * (1 - lines)} opacity={0.45 + index * 0.08} />
        ))}
      </svg>
      <div className="ev-lens-copy"><span>LOOK CLOSER</span><h2>DETAIL<br /><em>changes meaning.</em></h2></div>
      <Grain />
    </AbsoluteFill>
  );
};

const Spotlight: React.FC<{
  angle: number;
  length: number;
  width: number;
  opacity: number;
}> = ({angle, length, width, opacity}) => (
  <div
    className="ev-spotlight"
    style={{
      width,
      height: length,
      opacity,
      transform: `translateX(-50%) rotate(${angle}deg)`,
    }}
  />
);

const DarkStage: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = windowOpacity(frame, [0, 18], [108, 132]);
  const zoom = interpolate(frame, [0, 118], [1.28, 0.58], {
    ...clamp,
    easing: cameraEase,
  });
  const bloom = spring({
    frame,
    fps: 30,
    config: {damping: 20, stiffness: 95},
  });
  const lights = p(frame, 16, 66);

  return (
    <AbsoluteFill className="ev-dark" style={{opacity}}>
      <div className="ev-dark-camera" style={{transform: `scale(${zoom})`}}>
        <div className="ev-light-pool" />
        <div className="ev-stage-bloom" style={{transform: `translate(-50%, -50%) scale(${0.6 + bloom * 0.4})`}}>
          <ChromeBloom frame={frame} compact />
        </div>
        <div className="ev-spotlights">
          <Spotlight angle={-30} length={830} width={170} opacity={lights * 0.72} />
          <Spotlight angle={0} length={900} width={210} opacity={lights * 0.9} />
          <Spotlight angle={34} length={780} width={150} opacity={lights * 0.64} />
        </div>
      </div>
      <div className="ev-stage-copy"><span>NOT ONLY SCALE</span><h2>CREATE<br /><strong>DEPTH.</strong></h2></div>
      <Grain dark />
    </AbsoluteFill>
  );
};

const SystemNode: React.FC<{
  x: number;
  active: number;
  frame: number;
  delay: number;
}> = ({x, active, frame, delay}) => {
  const enter = spring({
    frame: frame - delay,
    fps: 30,
    config: {damping: 19, stiffness: 105},
  });
  const light = p(frame, delay + 8, delay + 36);

  return (
    <div className="ev-system-node" style={{left: x, opacity: enter, transform: `translate(-50%, -50%) scale(${0.45 + enter * 0.55})`}}>
      <div className="ev-node-light" style={{opacity: light * active, transform: `translateX(-50%) rotate(${(x / 12) % 22 - 11}deg)`}} />
      <div className="ev-node-disc">
        <ChromeBloom frame={frame + delay} compact />
      </div>
    </div>
  );
};

const SystemZoom: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = windowOpacity(frame, [0, 16], [124, 150]);
  const camera = interpolate(frame, [0, 145], [1.32, 0.62], {
    ...clamp,
    easing: cameraEase,
  });
  const worldX = interpolate(frame, [0, 145], [0, -180], {
    ...clamp,
    easing: cameraEase,
  });

  return (
    <AbsoluteFill className="ev-dark" style={{opacity}}>
      <div
        className="ev-system-camera"
        style={{transform: `translateX(${worldX}px) scale(${camera})`}}
      >
        <div className="ev-system-line" />
        {[90, 330, 570, 810, 1050, 1290, 1530].map((x, index) => (
          <SystemNode
            key={x}
            x={x}
            frame={frame}
            delay={index * 7}
            active={index % 3 === 0 ? 1 : 0.58}
          />
        ))}
        <div className="ev-system-frames">
          {[100, 360, 620, 880, 1140, 1400].map((x) => <i key={x} style={{left: x}} />)}
        </div>
      </div>
      <div className="ev-system-copy">
        <span>ONE OBJECT / MANY RELATIONSHIPS</span>
        <h2>BUILD A<br /><strong>VISUAL SYSTEM.</strong></h2>
      </div>
      <Grain dark />
    </AbsoluteFill>
  );
};

export const EditorialViralReel: React.FC = () => {
  const frame = useCurrentFrame();
  const end = interpolate(frame, [450, 470], [1, 0], {
    ...clamp,
    easing: Easing.in(Easing.cubic),
  });

  return (
    <AbsoluteFill style={{backgroundColor: T.paper, opacity: end, overflow: "hidden"}}>
      <Sequence durationInFrames={82} premountFor={30}>
        <IntroAnnotations />
      </Sequence>
      <Sequence from={55} durationInFrames={118} premountFor={30}>
        <PanelReveal />
      </Sequence>
      <Sequence from={150} durationInFrames={88} premountFor={30}>
        <LensAnalysis />
      </Sequence>
      <Sequence from={215} durationInFrames={132} premountFor={30}>
        <DarkStage />
      </Sequence>
      <Sequence from={320} durationInFrames={150} premountFor={30}>
        <SystemZoom />
      </Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
